export type BaseResponse<T = unknown> = {
  success: boolean;
  data: T;
};

export type ApiBool = boolean | 0 | 1;
export interface Country {
  id: number;
  name: string;
  isPopular: boolean;
  position: number | null;
  visaRequired: boolean;
  visaOnline: boolean;
  resorts?: Resort[];
  visaDays: number | null;
  minPrice: number | null;
}
export interface NightsRange {
  from: number;
  to: number;
}

export interface DateRange {
  from: string;
  to: string;
  daysFlexible: number;
}

export interface Resort {
  id: number;
  name: string;
  countryId: number;
  countryName: string;
  isPopular: boolean;
  minPrice: number | null;
  atFiltering: boolean;
}

export interface Hotel {
  id: number;
  name: string;
  countryId: number;
  countryName: string;
}
export type DestinationKind = 'country' | 'resort' | 'hotel';
export type Destination =
  | { type: 'country'; country: Country; resort?: null; hotel?: null }
  | { type: 'resort'; country?: null; resort: Resort; hotel?: null }
  | { type: 'hotel'; country?: null; resort?: null; hotel: Hotel };

export enum DepartureCityType {
  Capital = 'capital',
  Region = 'region',
}

export interface DepartureCity {
  id: number;
  name: string;
  nameEn?: string;
  type?: DepartureCityType;
  daysRange?: number;
  disabled?: boolean;
  disabledRailways?: boolean;
  countriesWithDirectFlights?: number[];
}
export interface CityTypeCriteria {
  nights?: NightsRange;
  dateRange?: DateRange;
}

export interface CountryCriteria {
  capital?: CityTypeCriteria;
  region?: CityTypeCriteria;
}

export type DefaultCriteriaIndex = Record<number, CountryCriteria>;

export interface SearchFormData {
  countries: Country[];
  cities: DepartureCity[];
  defaultCriteria: DefaultCriteriaIndex;
}
export interface TouristGroupRoom {
  adults: number;
  ages: number[];
  infants: number;
  kids: number;
}

export interface SearchCriteriaParams {
  checkInDateRange: { from: string; to: string; daysFlexible?: number };
  countries: number[];
  departureCity: { id: number; name: string };
  nightRange: NightsRange;
  resorts?: number[];
  hotels?: number[];
  meals?: number[];
  hotelCategories?: number[];
  directFlight?: boolean;
  block?: boolean;
  touristGroup: TouristGroupRoom & { touristsByRooms?: TouristGroupRoom[] };
}
interface HotelCoords {
  latitude: number;
  longitude: number;
}

interface HotelDistances {
  airport: number | null;
  beach: number | null;
  beachMax: number | null;
  lift: number | null;
  center: number | null;
}
export interface HotelProductDTO {
  id: number;
  name: string;
  country: number;
  resort: number;
  hotelCategory: number;
  attributes: number[];
  types: number[];
  coords: HotelCoords;
  distances: HotelDistances;
  cover: string;
  photos: string[];
  rating: number | null;
  reviewsNumber: number;
  ordersNumber: number;
  beachLine: number | null;
  builtDate: number | null;
  renovationDate: number | null;
  placement: string | null;
}

export interface ProductTouristGroup {
  adults: number;
  kids: number;
  infants: number;
  kidsAges: number[];
}

export interface TourNights {
  tour: number;
  hotel: number;
}

export interface FirstPaymentDefinitionsDTO {
  id: number;
  operator: number;
  firstPaymentPart: number;
  daysAfterReservationForFullPayment: number | null;
  daysBeforeCheckInDateForFullPayment: number | null;
  daysBeforeFreeCancellationDateForFullPayment: number | null;
  minDaysAvailableBeforeCheckInDate: number | null;
  minDaysAvailableBeforeFreeCancellationDate: number | null;
}

export interface RoomGroupDTO {
  hash: string;
  room: string;
  touristGroup: ProductTouristGroup;
}

export interface ProductRoomDTO {
  id: string;
  name: string;
}

export interface OperatorProductDTO {
  id: number;
  name: string;
  nameRu: string;
}
export interface CountryNoteDTO {
  id: number;
  imageUrl: string;
}
export interface CountryProductDTO {
  id: number;
  name: string;
  cover: string;
  notes: CountryNoteDTO[];
}
export interface TourProductDTO {
  id: string;
  price: number;
  maxHistoricalPrice: number;
  discount: number;
  sortRate: number;
  checkInDate: string;
  departureCity: number;
  country: number;
  resort: number;
  hotel: number;
  hotelCategory: number;
  hotelAvailable: number;
  nights: TourNights;
  meal: number;
  operator: number;
  room: string | ProductRoomDTO;
  touristGroup: ProductTouristGroup;
  flightType: string;
  transfer: string;
  chairTypeBlock?: boolean;
  freeCancellation: boolean;
  cashbackAvailable: boolean;
  medicalInsurance: boolean;
  oilTax: number;
  roomGroups?: RoomGroupDTO[];
  firstPaymentDefinitions: number[];
}

export interface HotelOfferProductDTO {
  id: string;
  price: number;
  maxHistoricalPrice: number;
  discount: number;
  sortRate: number;
  checkInDate: string;
  country: number;
  resort: number;
  hotel: number;
  hotelCategory: number;
  hotelAvailable: number;
  nights: number;
  meal: number;
  operator: number;
  room: string | ProductRoomDTO;
  touristGroup: ProductTouristGroup;
  passportType: number;
  cashbackAvailable: boolean;
  medicalInsurance: boolean;
  firstPaymentDefinitions: number[];
}

export interface RailwayProductDTO {
  id: string;
  price: number;
  maxHistoricalPrice: number;
  discount: number;
  sortRate: number;
  checkInDate: string;
  departureCity: number;
  country: number;
  resort: number;
  hotel: number;
  hotelCategory: number;
  hotelAvailable: number;
  nights: TourNights;
  meal: number;
  operator: number;
  room: string | ProductRoomDTO;
  touristGroup: ProductTouristGroup;
  cashbackAvailable: boolean;
  medicalInsurance: boolean;
  firstPaymentDefinitions: number[];
  routes: string[];
}
